(() => {
  if (window.vesmereEditorial) return;
  window.vesmereEditorial = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 1000px)');
  const instances = new Map();
  class EditorialSection {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.animations = new Set();
      this.active = false;
      this.frame = 0;
      this.ready = false;
      const signal = this.controller.signal;
      this.targets = [...root.querySelectorAll('[data-vm-reveal]')];
      if (root.dataset.vmMotion !== 'false' && !reduced.matches && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            if (entry.target.animate) {
              const animation = entry.target.animate([
                { opacity: 0.35, transform: 'translateY(22px)' },
                { opacity: 1, transform: 'translateY(0)' }
              ], { duration: 700, easing: 'cubic-bezier(.22,1,.36,1)', delay: Number(entry.target.dataset.vmDelay || 0) });
              this.animations.add(animation);
              animation.finished.then(() => this.animations.delete(animation)).catch(() => {});
            }
            this.observer.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        this.targets.forEach(target => this.observer.observe(target));
      }
      this.media = root.querySelector('[data-vm-parallax]');
      if (this.media && root.dataset.vmMotion !== 'false' && root.dataset.vmParallax === 'true' && 'IntersectionObserver' in window) {
        this.mediaObserver = new IntersectionObserver(entries => {
          this.active = entries[0].isIntersecting;
          this.queue();
        });
        this.mediaObserver.observe(root);
        window.addEventListener('scroll', () => this.queue(), { passive: true, signal });
        window.addEventListener('resize', () => this.queue(), { passive: true, signal });
        reduced.addEventListener('change', () => this.queue(), { signal });
        wide.addEventListener('change', () => this.queue(), { signal });
      }
      this.scroller = root.querySelector('[data-vm-scroller]');
      if (this.scroller) {
        this.progress = root.querySelector('[data-vm-progress]');
        const update = () => {
          const max = this.scroller.scrollWidth - this.scroller.clientWidth;
          const fraction = max > 1 ? Math.min(1, Math.abs(this.scroller.scrollLeft) / max) : 0;
          if (this.progress) {
            this.progress.hidden = max <= 1;
            this.progress.style.setProperty('--vm-progress', fraction);
          }
        };
        this.scroller.addEventListener('scroll', update, { passive: true, signal });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(update);
          this.resizeObserver.observe(this.scroller);
        }
        update();
      }
      root.addEventListener('shopify:block:select', event => {
        const selected = event.target.closest('[data-vm-card]');
        if (selected && this.scroller) this.scroller.scrollTo({ left: selected.offsetLeft - this.scroller.offsetLeft, behavior: 'instant' });
      }, { signal });
      reduced.addEventListener('change', () => {
        if (!reduced.matches) return;
        this.observer?.disconnect();
        this.animations.forEach(animation => animation.cancel());
      }, { signal });
    }
    queue() {
      if (!this.media) return;
      if (reduced.matches || !wide.matches || window.Shopify?.designMode) {
        this.media.style.removeProperty('transform');
        return;
      }
      if (!this.active || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        const rect = this.root.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, rect.top / Math.max(rect.height, 1)));
        this.media.style.transform = `translate3d(0,${progress * -24}px,0) scale(1.055)`;
      });
    }
    destroy() {
      this.controller.abort();
      this.observer?.disconnect();
      this.mediaObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.animations.forEach(animation => animation.cancel());
      cancelAnimationFrame(this.frame);
    }
  }
  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-vm-section]')];
    if (scope.matches?.('[data-vm-section]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new EditorialSection(root));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (event.target.contains(root)) { instance.destroy(); instances.delete(root); }
    });
  });
})();
